import { ReportResponse } from '@/types.d';
import { formatReportDate } from '@/src/helpers/formatReportDate';
import { formatScrapeReviewValue } from '@/src/helpers/admin/formatScrapeReviewValue';
import { reportMeetingName } from '@/src/helpers/reportMeetingName';
import { meetingIssues } from '@/src/defaults/selectsDefaults';

/**
 * Composition for the meeting-report surfaces.
 *
 * These values are attacker-controllable: `note` comes from a textarea on the
 * public compass and is stored with no validation, and `name` comes from the
 * scraper. They used to be interpolated into HTML strings and rendered through
 * `dangerouslySetInnerHTML`. Composing React nodes here instead means React
 * escapes every interpolated value, and it puts that guarantee somewhere a
 * business-logic test can assert on it directly.
 *
 * Every document-sourced value goes through a formatter before it becomes a
 * React child, and that is not optional here. `firestore.rules` allows
 * unauthenticated writes to `reports`, and `fetchMeetingReports` casts rather
 * than parses, so a field can hold any shape at all. A template literal used to
 * absorb that — an object rendered as `[object Object]`. A React child does not:
 * it throws, and with no error boundary between this component and
 * `app/error.tsx` the whole reported-meetings screen dies. It would not
 * self-heal either, because `ReportedMeetings` auto-focuses the newest report,
 * so a single crafted document takes the screen down on every load and the UI
 * needed to resolve it is the UI that crashed.
 */

const labelled = (label: string, value: React.ReactNode): JSX.Element => (
  <>
    <strong>{label}</strong> {value}
  </>
);

export type ReportFieldKey = 'issue' | 'note' | 'dateSubmitted' | 'meetingId';

export interface ReportField {
  key: ReportFieldKey;
  content: JSX.Element;
}

export const composeReportFields = (report: ReportResponse): ReportField[] => [
  {
    key: 'issue',
    content: labelled(
      'Issue:',
      // Same reasoning as the date below: a reviewer acts on this field, and a
      // blank is indistinguishable from a rendering bug. Say the value was not
      // recognised. The label itself comes from our own constant, never from
      // the document, so the lookup is also what keeps this field inert.
      meetingIssues.find((cur) => cur.value === report.issue)?.label ??
        '(unrecognised issue)'
    ),
  },
  {
    key: 'note',
    content: labelled('Details:', formatScrapeReviewValue(report.note)),
  },
  {
    key: 'dateSubmitted',
    content: labelled(
      'Date submitted:',
      formatReportDate(report.dateSubmitted)
    ),
  },
  {
    key: 'meetingId',
    content: labelled('Meeting ID:', formatScrapeReviewValue(report.meetingId)),
  },
];

export const composeReportPrompt = (meetingName: string): JSX.Element => (
  <>
    Please provide some details about the issue you encountered with{' '}
    <strong>{reportMeetingName(meetingName)}</strong>:
  </>
);
